/**
 * Wires up the inline preview editor.
 *
 * Runs on every shell-rendered page (mounted in App.tsx). Activates
 * edit mode when ``useEditModeAccess()`` reports ``isActive`` (URL has
 * ``?_edit=1`` AND the user has tenant-admin role on the resolved
 * subdomain). When active, sets the ``editModeStore`` flag so the
 * i18next post-processor begins wrapping editable strings, and renders
 * the top bar + DOM overlay.
 *
 * Anyone else gets a no-op — the editor bundle is dynamically imported
 * only when the gate passes, so anonymous visitors / non-admin sessions
 * pay zero JS.
 */

import { lazy, Suspense, useEffect, type JSX } from "react";

import {
  editModeStore,
  MARKER_OPEN,
  stripEditMarkers,
} from "@/components/edit/editModeStore";
import { useEditModeAccess } from "@/components/edit/useEditModeAccess";
import i18n from "@/i18n";

const EditOverlayLayer = lazy(() =>
  import("@/components/edit/EditOverlayLayer").then((m) => ({
    default: m.EditOverlayLayer,
  })),
);

const EditModeBar = lazy(() =>
  import("@/components/edit/EditModeBar").then((m) => ({
    default: m.EditModeBar,
  })),
);

const TEXT_ATTRIBUTES = ["aria-label", "title", "alt", "placeholder"] as const;

/**
 * Značky editoru patří jen do textových uzlů. Překladový post-procesor je ale
 * přidá do každého řetězce, tedy i do ``aria-label`` / ``title`` / ``alt``, kde je
 * čtečka obrazovky a tooltip ukazují jako neviditelný šum (KAN-1816). Tady se z
 * atributů hned odstraní.
 */
function sanitizeAttributes(root: ParentNode): void {
  for (const attr of TEXT_ATTRIBUTES) {
    for (const el of root.querySelectorAll(`[${attr}]`)) {
      const value = el.getAttribute(attr);
      if (value && value.includes(MARKER_OPEN)) {
        el.setAttribute(attr, stripEditMarkers(value));
      }
    }
  }
}

function useEditMarkerSanitizer(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    sanitizeAttributes(document);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.target instanceof Element) {
          const name = m.attributeName ?? "";
          const value = m.target.getAttribute(name);
          if (value && value.includes(MARKER_OPEN)) {
            m.target.setAttribute(name, stripEditMarkers(value));
          }
        } else if (m.type === "childList") {
          for (const node of m.addedNodes) {
            if (node instanceof Element) {
              sanitizeAttributes(node);
              for (const attr of TEXT_ATTRIBUTES) {
                const v = node.getAttribute(attr);
                if (v && v.includes(MARKER_OPEN)) {
                  node.setAttribute(attr, stripEditMarkers(v));
                }
              }
            }
          }
        }
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [...TEXT_ATTRIBUTES],
    });
    return () => observer.disconnect();
  }, [active]);
}

export function EditModeBootstrap(): JSX.Element | null {
  const { isActive } = useEditModeAccess();
  useEditMarkerSanitizer(isActive);

  useEffect(() => {
    editModeStore.set(isActive);
    // Force every ``useTranslation`` consumer to re-render so the
    // post-processor's new ``isActive`` value flows through. The
    // canonical re-render trigger is ``languageChanged`` on the
    // i18n instance — react-i18next's ``useTranslation`` listens
    // for it via ``i18n.on('languageChanged', resetT)``. (The
    // ``added`` / ``removed`` events live on ``i18n.store`` and
    // wouldn't fire by emitting on the bare instance.)
    i18n.emit("languageChanged", i18n.language);
    return () => {
      editModeStore.set(false);
      i18n.emit("languageChanged", i18n.language);
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <Suspense fallback={null}>
      <EditModeBar />
      <EditOverlayLayer />
    </Suspense>
  );
}
